import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, X, Plus, Play } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ImageUploadInput from "../ImageUploadInput";

const API = process.env.REACT_APP_BACKEND_URL;

async function uploadToStorage(file) {
  const token = localStorage.getItem("it_token");
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/api/upload/image`, form, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return `${API}${res.data.url}`;
}

export default function MediaTab({ data, onChange }) {
  const mainRef = useRef();
  const galleryRef = useRef();
  const [mainUploading, setMainUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const videoRef = useRef();
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoMode, setVideoMode] = useState(
    data.video_url && !data.video_url.includes("/api/images/") ? "url" : "upload"
  );

  const handleVideoFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!/\.(mp4|webm)$/i.test(file.name)) { toast.error("Only MP4 or WebM videos"); e.target.value = ""; return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("Max 50MB"); e.target.value = ""; return; }
    setVideoUploading(true);
    try {
      const token = localStorage.getItem("it_token");
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/api/upload/media`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange("video_url", `${API}${res.data.url}`);
      toast.success("Video uploaded");
    } catch { toast.error("Video upload failed"); }
    finally { setVideoUploading(false); e.target.value = ""; }
  };

  const handleMainImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setMainUploading(true);
    try {
      const url = await uploadToStorage(file);
      onChange("main_image", url);
    } catch { toast.error("Upload failed"); }
    finally { setMainUploading(false); e.target.value = ""; }
  };

  const handleGalleryImages = async (e) => {
    const files = Array.from(e.target.files);
    const remaining = 10 - (data.gallery_images?.length || 0);
    if (remaining <= 0) { toast.error("Max 10 gallery images"); return; }
    const toProcess = files.slice(0, remaining);
    setGalleryUploading(true);
    try {
      const urls = await Promise.all(toProcess.map(f => uploadToStorage(f)));
      onChange("gallery_images", [...(data.gallery_images || []), ...urls]);
    } catch { toast.error("Gallery upload failed"); }
    finally { setGalleryUploading(false); e.target.value = ""; }
  };

  const removeGallery = (idx) => {
    const updated = data.gallery_images.filter((_, i) => i !== idx);
    onChange("gallery_images", updated);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">2</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Media</h2>
      </div>

      {/* Product Images */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span className="w-4 h-4 bg-orange-500 rounded text-white text-xs flex items-center justify-center">P</span>
          Product Images
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Main Image */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Main Image <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-white">
              {data.main_image ? (
                <div className="relative">
                  <img
                    src={data.main_image}
                    alt="Main"
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => onChange("main_image", "")}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  className="h-48 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => mainRef.current?.click()}
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3">
                    {mainUploading
                      ? <div className="w-5 h-5 border-2 border-orange-500/40 border-t-orange-500 rounded-full animate-spin" />
                      : <Upload className="w-5 h-5 text-orange-500" />
                    }
                  </div>
                  <p className="text-sm font-medium text-slate-700">{mainUploading ? "Uploading…" : "Upload Main Image"}</p>
                  <p className="text-xs text-slate-400 mt-1">Recommended: 1000 × 1000 px</p>
                  <p className="text-xs text-slate-400">Max file size: 10 MB</p>
                </div>
              )}
            </div>
            <button
              onClick={() => mainRef.current?.click()}
              disabled={mainUploading}
              className="mt-2 w-full border border-slate-300 text-slate-700 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-50 transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" />
              {mainUploading ? "Uploading…" : "Upload Main Image"}
            </button>
            <input ref={mainRef} type="file" accept="image/*" className="hidden" onChange={handleMainImage} />

            {/* Also allow URL input */}
            <div className="mt-2">
              <input
                type="url"
                value={data.main_image?.startsWith("data:") ? "" : (data.main_image || "")}
                onChange={e => onChange("main_image", e.target.value)}
                placeholder="Or paste image URL..."
                className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
              />
            </div>
          </div>

          {/* Gallery Images */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Gallery Images (Max 10)
            </label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {(data.gallery_images || []).map((img, idx) => (
                <div key={idx} className="relative aspect-square border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeGallery(idx)}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {(data.gallery_images || []).length < 10 && (
                <div
                  className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => !galleryUploading && galleryRef.current?.click()}
                >
                  {galleryUploading
                    ? <div className="w-4 h-4 border-2 border-orange-500/40 border-t-orange-500 rounded-full animate-spin" />
                    : <Plus className="w-4 h-4 text-slate-400" />
                  }
                  <p className="text-xs text-slate-400 mt-1 text-center">
                    {galleryUploading ? "Uploading…" : <span>Add Images<br />(Max 10)</span>}
                  </p>
                </div>
              )}
            </div>
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleGalleryImages}
            />
            <p className="text-xs text-slate-400">
              {(data.gallery_images || []).length}/10 images added
            </p>
          </div>
        </div>
      </div>

      {/* Product Video */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Product Video</h3>

        <div className="flex gap-4 mb-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="video_type" checked={videoMode === "upload"} onChange={() => setVideoMode("upload")} className="accent-orange-500" />
            <span className="text-sm text-slate-700">Upload Video</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="video_type" checked={videoMode === "url"} onChange={() => setVideoMode("url")} className="accent-orange-500" />
            <span className="text-sm text-slate-700">Video URL</span>
          </label>
        </div>

        <div className="flex gap-3 items-start">
          {data.video_url ? (
            <div className="relative border border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center w-32 h-24 flex-shrink-0">
              <Play className="w-8 h-8 text-slate-400" />
              <div className="absolute inset-0 flex items-end justify-start p-2">
                <span className="text-xs text-slate-600 bg-white/80 px-1 rounded truncate max-w-full">
                  {data.video_url.split("/").pop() || "video.mp4"}
                </span>
              </div>
              <button
                onClick={() => onChange("video_url", "")}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ) : null}
          <div className="flex-1">
            {videoMode === "upload" ? (
              <div>
                <input
                  ref={videoRef}
                  type="file"
                  accept="video/mp4,video/webm,.mp4,.webm"
                  className="hidden"
                  onChange={handleVideoFile}
                  data-testid="product-video-file-input"
                />
                <button
                  type="button"
                  disabled={videoUploading}
                  onClick={() => videoRef.current?.click()}
                  data-testid="product-video-upload-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:border-orange-500 hover:text-orange-600 disabled:opacity-60"
                >
                  <Upload className="w-4 h-4" />
                  {videoUploading ? "Uploading..." : data.video_url ? "Replace video" : "Choose video file"}
                </button>
                <p className="text-xs text-slate-400 mt-1">MP4 or WebM (Max 50 MB)</p>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="url"
                    value={data.video_url || ""}
                    onChange={e => onChange("video_url", e.target.value.trim())}
                    placeholder="Enter video URL (YouTube, Vimeo, or direct MP4 link)..."
                    data-testid="product-video-url-input"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">YouTube, Vimeo, or a direct .mp4 / .webm link</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Media Description */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Media Description (Optional)</h3>
        <textarea
          value={data.media_description}
          onChange={e => onChange("media_description", e.target.value)}
          placeholder="Add short description about images or video (e.g. show product from different angles, usage, etc.)"
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 resize-none"
        />
        <p className="text-right text-xs text-slate-400 mt-1">
          {(data.media_description || "").length}/500
        </p>
      </div>
    </div>
  );
}
